import React, { useEffect, useState } from 'react';
import { Lock, Sparkles, ShieldCheck, ArrowRightCircle, Trophy, CheckCircle2 } from 'lucide-react';
import authAxios from '../auth/authAxios';
import { Link, useParams } from 'react-router-dom';

interface Lesson {
  lesson_id: string;
  title: string;
  description: string;
  completed: boolean;
  objective?: string;
  steps?: string;
  hint?: string;
  example_prompt?: string;
  max_score?: number;
}

interface Challenge {
  challenge_id: string;
  title: string;
  description: string;
  badge_icon: string;
  passing_score: number;
  lessons: Lesson[];
}

const ChallengeDetailPage = () => {
  const { challenge_id } = useParams<{ challenge_id: string }>();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [finalScore, setFinalScore] = useState<number | null>(null);

  useEffect(() => {
    authAxios.get(`/api/challenges/${challenge_id}`)
      .then(res => setChallenge(res.data))
      .catch(err => console.error('Failed to load challenge:', err));
  }, [challenge_id]);

  const nextLesson = () => {
    if (!challenge) return;
    if (currentIndex + 1 < challenge.lessons.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      const scoreSum = challenge.lessons.reduce((sum, l) => sum + (l.max_score || 0), 0);
      const avg = scoreSum / challenge.lessons.length;
      setFinalScore(avg);
    }
  };

  if (!challenge) return <p className="p-6">Loading challenge...</p>;

  const isLocked = (index: number) => {
    return index > 0 && !challenge.lessons[index - 1].completed;
  };

  return (
    <main className="p-8 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-primary mb-4 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-primary" /> {challenge.title}
      </h2>
      <p className="text-white/80 mb-8">{challenge.description}</p>

      {challenge.lessons.every(l => l.completed) && (
        <div className="flex text-yellow-400 mt-6 mb-6 w-full">
          <Trophy className="w-8 h-8 mr-2" />
          <span className="text-lg font-bold">Challenge Complete!</span>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {challenge.lessons.map((lesson, index) => {
          const isLocked = index > 0 && !challenge.lessons[index - 1].completed;

          const CardContent = () => (
            <>
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-white font-semibold text-md">
                  {lesson.title}
                </h4>
                {lesson.completed ? (
                  <CheckCircle2 className="text-green-400 w-4 h-4" />
                ) : isLocked ? (
                  <Lock className="text-white/40 w-4 h-4" />
                ) : <Sparkles className="text-yellow-400 w-4 h-4" />}
              </div>
              <p className="text-white/70 text-sm mb-2 line-clamp-2">{lesson.description}</p>
              {lesson.completed && lesson.max_score && (
                <p className="text-xs text-white/60">Score: {lesson.max_score.toFixed(1)}</p>
              )}
              {!isLocked && (
                <span className="mt-2 inline-block text-sm text-primary hover:underline">
                  {lesson.completed ? "Restart Lesson" : "Start Lesson"}
                </span>
              )}
            </>
          );

          return isLocked ? (
            <div
              key={lesson.lesson_id}
              className="w-64 p-4 rounded-xl border shadow transition-all border-white/10 bg-white/5 opacity-50 backdrop-blur-sm"
            >
              <CardContent />
            </div>
          ) : (
            <Link
              key={lesson.lesson_id}
              to={`/challenges/${challenge.challenge_id}/lessons/${lesson.lesson_id}`}
              className={`w-64 p-4 rounded-xl border shadow transition-all ${lesson.completed
                  ? 'border-green-500 bg-green-900/20'
                  : 'border-white/10 bg-white/10'
                } backdrop-blur-sm hover:shadow-lg hover:scale-[1.05] hover:bg-cyan-500/20 transition-transform duration-200 cursor-pointer`}
            >
              <CardContent />
            </Link>
          );
        })}

      </div>
    </main>
  );

};

export default ChallengeDetailPage;
